import { BreakTime, isNextDayEnd, validateBreakTime } from 'apis/types';
import { LabeledReadOnlyInput, ReadOnlyInput } from 'components/Fields';

interface BreakTimeProps {
  startTime?: string[];
  endTime?: string[];
  breakTimeTypeLabel: string;
  // 종료시간 익일 여부 (서버 플래그, 없으면 종료 < 시작 휴리스틱)
  endIsNext?: boolean | null;
  // 시작시간 익일 여부 (자정 넘김 영업의 새벽 브레이크타임)
  startIsNext?: boolean | null;
}

interface BreakTimesProps {
  breakTimes: BreakTimeProps[];
  isMobile?: boolean;
}

interface BreakTimeForFirstChildProps extends BreakTimeProps {
  isMobile?: boolean;
}

export default function BreakTimes(breakTime: BreakTime, isMobile: boolean) {
  switch (breakTime.breakTimeType) {
    case 'DAILY':
      return (
        <BreakTimeForFirstChild
          startTime={breakTime.mondayStartTime}
          endTime={breakTime.mondayEndTime}
          breakTimeTypeLabel="매일"
          endIsNext={breakTime.mondayEndIsNext}
          startIsNext={breakTime.mondayStartIsNext}
          isMobile={isMobile}
        />
      );
    case 'WEEKDAY_WEEKEND':
      return BreakTimeList({
        breakTimes: [
          {
            startTime: breakTime.mondayStartTime,
            endTime: breakTime.mondayEndTime,
            breakTimeTypeLabel: '주중',
            endIsNext: breakTime.mondayEndIsNext,
            startIsNext: breakTime.mondayStartIsNext,
          },
          {
            startTime: breakTime.sundayStartTime,
            endTime: breakTime.sundayEndTime,
            breakTimeTypeLabel: '주말',
            endIsNext: breakTime.sundayEndIsNext,
            startIsNext: breakTime.sundayStartIsNext,
          },
        ],
        isMobile: isMobile,
      });
    default:
      return BreakTimeList({
        breakTimes: [
          {
            startTime: breakTime.mondayStartTime,
            endTime: breakTime.mondayEndTime,
            breakTimeTypeLabel: '월요일',
            endIsNext: breakTime.mondayEndIsNext,
            startIsNext: breakTime.mondayStartIsNext,
          },
          {
            startTime: breakTime.tuesdayStartTime,
            endTime: breakTime.tuesdayEndTime,
            breakTimeTypeLabel: '화요일',
            endIsNext: breakTime.tuesdayEndIsNext,
            startIsNext: breakTime.tuesdayStartIsNext,
          },
          {
            startTime: breakTime.wednesdayStartTime,
            endTime: breakTime.wednesdayEndTime,
            breakTimeTypeLabel: '수요일',
            endIsNext: breakTime.wednesdayEndIsNext,
            startIsNext: breakTime.wednesdayStartIsNext,
          },
          {
            startTime: breakTime.thursdayStartTime,
            endTime: breakTime.thursdayEndTime,
            breakTimeTypeLabel: '목요일',
            endIsNext: breakTime.thursdayEndIsNext,
            startIsNext: breakTime.thursdayStartIsNext,
          },
          {
            startTime: breakTime.fridayStartTime,
            endTime: breakTime.fridayEndTime,
            breakTimeTypeLabel: '금요일',
            endIsNext: breakTime.fridayEndIsNext,
            startIsNext: breakTime.fridayStartIsNext,
          },
          {
            startTime: breakTime.saturdayStartTime,
            endTime: breakTime.saturdayEndTime,
            breakTimeTypeLabel: '토요일',
            endIsNext: breakTime.saturdayEndIsNext,
            startIsNext: breakTime.saturdayStartIsNext,
          },
          {
            startTime: breakTime.sundayStartTime,
            endTime: breakTime.sundayEndTime,
            breakTimeTypeLabel: '일요일',
            endIsNext: breakTime.sundayEndIsNext,
            startIsNext: breakTime.sundayStartIsNext,
          },
        ],
        isMobile: isMobile,
      });
  }
}

const BreakTimeList = ({ breakTimes, isMobile = false }: BreakTimesProps) => {
  return breakTimes
    .filter(({ startTime, endTime }) => validateBreakTime(startTime, endTime))
    .map((item, i) => {
      if (i === 0) {
        return (
          <BreakTimeForFirstChild
            key={i}
            startTime={item.startTime}
            endTime={item.endTime}
            breakTimeTypeLabel={item.breakTimeTypeLabel}
            endIsNext={item.endIsNext}
            startIsNext={item.startIsNext}
            isMobile={isMobile}
          />
        );
      }

      return (
        <BreakTimeConponent
          key={i}
          startTime={item.startTime}
          endTime={item.endTime}
          breakTimeTypeLabel={item.breakTimeTypeLabel}
          endIsNext={item.endIsNext}
          startIsNext={item.startIsNext}
        />
      );
    });
};

const BreakTimeForFirstChild = ({
  startTime,
  endTime,
  breakTimeTypeLabel,
  endIsNext,
  startIsNext,
  isMobile,
}: BreakTimeForFirstChildProps) => {
  if (!validateBreakTime(startTime, endTime)) return null;

  const startNextDay = startIsNext === true;
  // 시작이 익일이면 종료도 같은 날(익일)이므로 종료 prefix는 시작 기준과 중복 표기하지 않음
  const nextDay = startNextDay || isNextDayEnd(startTime, endTime, endIsNext);

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <LabeledReadOnlyInput
        label="휴게시간"
        value={breakTimeTypeLabel}
        isMobile={isMobile}
        isColumn={true}
        style={{ width: 100 }}
      />
      <LabeledReadOnlyInput
        label="시작시간"
        value={startTime ? `${startNextDay ? '익일 ' : ''}${startTime[0]}시` : '-'}
        isMobile={isMobile}
        isColumn={true}
        style={{ width: `calc((100% - 100px - 8px * 4) / 4)`, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={startTime ? `${startTime[1]}분` : '-'}
        style={{ marginTop: 'auto', width: `calc((100% - 100px - 8px * 4) / 4)`, minWidth: 60 }}
      />
      <LabeledReadOnlyInput
        label="종료시간"
        value={endTime ? `${nextDay ? '익일 ' : ''}${endTime[0]}시` : '-'}
        isMobile={isMobile}
        isColumn={true}
        style={{ width: `calc((100% - 100px - 8px * 4) / 4)`, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={endTime ? `${endTime[1]}분` : '-'}
        style={{ marginTop: 'auto', width: `calc((100% - 100px - 8px * 4) / 4)`, minWidth: 60 }}
      />
    </div>
  );
};

const BreakTimeConponent = ({
  startTime,
  endTime,
  breakTimeTypeLabel,
  endIsNext,
  startIsNext,
}: BreakTimeProps) => {
  if (!validateBreakTime(startTime, endTime)) return null;

  const startNextDay = startIsNext === true;
  const nextDay = startNextDay || isNextDayEnd(startTime, endTime, endIsNext);

  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: 4 }}>
      <ReadOnlyInput text={breakTimeTypeLabel} style={{ width: 100 }} />
      <ReadOnlyInput
        text={startTime ? `${startNextDay ? '익일 ' : ''}${startTime[0]}시` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={startTime ? `${startTime[1]}분` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput
        text={endTime ? `${nextDay ? '익일 ' : ''}${endTime[0]}시` : '-'}
        style={{ flex: 1, minWidth: 60 }}
      />
      <ReadOnlyInput text={endTime ? `${endTime[1]}분` : '-'} style={{ flex: 1, minWidth: 60 }} />
    </div>
  );
};
